import React, { useState, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Formik as FormikBase, Field } from "formik";
import { Button } from "@mui/material";

import { passwordMDInputFormik } from "@/helpers/formInputs";
import {
  validCurrentPassword,
  validSecurePassConfPass,
} from "@/helpers/validations";
import LoadingButton from "@/components/loaders/LoadingButton";
import { authService } from "../services/authService";
import { HTTP_STATUS } from "@/config/constants";
import AlertBootstrapBase from "@/components/AlertBootstrap";
import { LoaderContext } from "@/helpers/contexts/contexts";
import MESSAGE from "@/helpers/messages";
import { loggedInUserSelector } from "@/features/profile/store/userSlice";
import { encodePassword } from "@/helpers/utils";

const Formik = FormikBase as unknown as React.ComponentType<any>;
const AlertBootstrap =
  AlertBootstrapBase as unknown as React.ComponentType<any>;

type CheckCurrentPasswordProps = {
  isCurrentPassValid?: boolean;
  onCancel: () => void;
  onChecked: (_value: unknown) => void;
  onUpdatePass: (_value: unknown) => void;
};

const CheckCurrentPassword = ({
  isCurrentPassValid,
  onChecked,
  onCancel,
  onUpdatePass,
}: CheckCurrentPasswordProps) => {
  const user = useSelector(loggedInUserSelector) as any;
  const loader = useContext(LoaderContext);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  /**
   * @method onSubmitForm
   * @description handle on submit form
   * @param {*} values
   */
  const onSubmitForm = async (values: any, { resetForm }: any) => {
    setErrorMessage("");
    setMessage("");
    setIsLoading(true);
    const data = {
      id: user.logedInUser ? user.logedInUser : user.id,
      password: encodePassword(values.password),
    };

    if (isCurrentPassValid) {
      // update password
      try {
        const res = await authService.updatePassword(data);

        if (HTTP_STATUS.CREATED === res.status) {
          if (res.data) {
            setMessage(MESSAGE.passwordUpdateSuccess);
            setTimeout(() => {
              onUpdatePass(res.data);
            }, 3000);
          } else {
            setErrorMessage(MESSAGE.passwordUpdateFail);
          }
        }
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setErrorMessage(MESSAGE.somethingWrong("update"));
      }
    } else {
      // verify current password
      try {
        const res = await authService.checkCurrentPassword(data);

        if (HTTP_STATUS.OK === res.status) {
          onChecked(res.data);
          if (res.data) {
            resetForm();
          } else {
            setErrorMessage(MESSAGE.passwordCurrentNotMatch);
          }
        }
        setIsLoading(false);
      } catch (err) {
        onChecked(false);
        setIsLoading(false);
        setErrorMessage(MESSAGE.somethingWrong("checking"));
      }
    }
  };

  useEffect(() => {
    loader(isLoading);
  }, [isLoading]);

  return (
    <div className="login-form">
      <div className="fs-14">
        {message ? (
          <h5 className="text-center">{message}</h5>
        ) : isCurrentPassValid ? (
          "Please enter new password"
        ) : (
          "Please enter your current password"
        )}
      </div>
      {errorMessage && (
        <AlertBootstrap
          show={errorMessage}
          variant="danger"
          className="mb-1"
          dismissible={false}
          persist={false}
        >
          {errorMessage}
        </AlertBootstrap>
      )}
      {!message && (
        <Formik
          initialValues={
            isCurrentPassValid
              ? { password: "", confirmPassword: "" }
              : { password: "" }
          }
          onSubmit={onSubmitForm}
          validate={
            isCurrentPassValid ? validSecurePassConfPass : validCurrentPassword
          }
          enableReinitialize
        >
          {({ handleSubmit }: any) => {
            return (
              <form onSubmit={handleSubmit}>
                <Field
                  name="password"
                  label="Password"
                  component={passwordMDInputFormik}
                  autoFocus={true}
                  autoComplete="new-password"
                  size="small"
                />
                {isCurrentPassValid && (
                  <Field
                    name="confirmPassword"
                    label="Confirm Password"
                    component={passwordMDInputFormik}
                    autoComplete="new-password"
                    size="small"
                  />
                )}
                <div className="form-group mt-2">
                  <div className="row">
                    <div className="col col-sm-6 text-center">
                      <LoadingButton
                        type="submit"
                        className="order-1 py-auto"
                        text={isCurrentPassValid ? "Set" : "Verify"}
                        isLoading={isLoading}
                        size="large"
                        fullWidth
                      />
                    </div>
                    <div className="col col-sm-6 text-center">
                      <Button
                        onClick={onCancel}
                        disabled={isLoading}
                        type="button"
                        variant="outlined"
                        color="primary"
                        className="mr-sm-2 order-0"
                        size="large"
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      )}
    </div>
  );
};

export default CheckCurrentPassword;
